import { formatCurrency } from '@/shared/lib/currency'
import {
  BalanceAction,
  BalanceActionTitles,
  CryptoWithdrawStatusIcons,
  CryptoWithdrawStatusTitles,
  FinanceModel,
} from '../model'
import { ColumnDef } from '@tanstack/react-table'
import { getShortenId } from '@/shared/utils/getShortenId'
import { FinanceIcon, ItemLayout, StatusLabel } from '@/shared/components'
import TransactionIcon from '@/shared/assets/icons/transactionArrow.svg?react'
import { parseBalanceComment } from '@/entities/balances/lib/parseBalanceComment'
import { formatDate } from '@/shared/lib/date'
import { BalanceTypeTitles } from '@/entities/balances'
import { Currencies } from '@/entities/curency'
import { truncateHash } from '@/shared/lib/hash'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

const CommentCell = ({ comment }: { comment: string }) => {
  if (!comment) return null
  return (
    <div className="max-w-xs text-sm break-words whitespace-pre-wrap">
      {parseBalanceComment(comment)}
    </div>
  )
}

export const getFinanceColumns = ({
                                    isDashboard,
                                  }: {
  isDashboard?: boolean
}): ColumnDef<FinanceModel>[] => [
  {
    accessorKey: 'id',
    header: () => <div>ID операции</div>,
    cell: ({ row }) => {
      const isCryptoWithdraw = row.original.cryptoWithdraw !== null
      const id = isCryptoWithdraw
        ? (row.original.cryptoWithdraw.id ?? '')
        : row.getValue<string>('id')
      return (
        <div className="flex flex-col">
          <CopyBlock text={id}
                     title={id}>
            {getShortenId(id)}
          </CopyBlock>
        </div>
      )
    },
  },
  {
    accessorKey: 'actionType',
    header: 'Действие',
    cell: ({ row }) => {
      const isCryptoWithdraw = row.original.cryptoWithdraw !== null
      const action = row.original.action
      const amount = row.original.amount
      const actionTitle = BalanceActionTitles[action]
      let transactionType: 'withdrawal' | 'deposit' = 'deposit'
      let statusColor: 'green' | 'red' | 'purple' = 'green'
      let type: 'sign' | 'arrow' = 'sign'

      if (isDashboard) {
        return (
          <div className="flex flex-col whitespace-nowrap">
            <span>{actionTitle}</span>
            <span className="text-text-secondary text-sm">
              {formatDate(row.original.createdAt, 'dateShortAndTimeWithoutSec')}
            </span>
          </div>
        )
      }

      if (isCryptoWithdraw) {
        const status = row.original.cryptoWithdraw.status
        const iconStatus = CryptoWithdrawStatusIcons[status]
        return (
          <ItemLayout
            title="Вывод прибыли"
            subtitle={formatDate(row.original.cryptoWithdraw.createdAt)}
            icon={<FinanceIcon transaction="withdrawal"
                               status={iconStatus}
                               type="arrow" />}
          />
        )
      }

      if (action === BalanceAction.CryptoWithdraw) {
        transactionType = 'withdrawal'
        statusColor = 'green'
        type = 'arrow'
      } else if (action === BalanceAction.CryptoDeposit) {
        transactionType = 'deposit'
        statusColor = 'green'
        type = 'arrow'
      } else if (action === BalanceAction.AdminChange && amount < 0) {
        transactionType = 'withdrawal'
        statusColor = 'purple'
        type = 'sign'
      } else if (action === BalanceAction.AdminChange && amount > 0) {
        transactionType = 'deposit'
        statusColor = 'purple'
        type = 'sign'
      } else if (
        (action === BalanceAction.BalanceTransfer || action === BalanceAction.Deposit) &&
        amount > 0
      ) {
        transactionType = 'deposit'
        statusColor = 'green'
        type = 'sign'
      } else if (
        (action === BalanceAction.BalanceTransfer || action === BalanceAction.Deposit) &&
        amount < 0
      ) {
        transactionType = 'withdrawal'
        statusColor = 'green'
        type = 'sign'
      }

      return (
        <ItemLayout
          title={actionTitle}
          subtitle={formatDate(row.original.createdAt)}
          icon={<FinanceIcon transaction={transactionType}
                             status={statusColor}
                             type={type} />}
        />
      )
    },
  },
  {
    accessorKey: 'balanceType',
    header: 'Тип баланса',
    cell: ({ row }) => {
      const isCryptoWithdraw = row.original.cryptoWithdraw !== null

      if (isCryptoWithdraw) {
        const status = row.original.cryptoWithdraw.status
        const title = CryptoWithdrawStatusTitles[status]
        const statusLabel = CryptoWithdrawStatusIcons[status]
        return <StatusLabel status={statusLabel}>{title}</StatusLabel>
      }

      const balanceType = row.original.balanceType
      const title = BalanceTypeTitles[balanceType]
      return <span className="text-sm">{title}</span>
    },
  },
  {
    accessorKey: 'amount',
    header: 'Сумма',
    cell: ({ row }) => {
      const isCryptoWithdraw = row.original.cryptoWithdraw !== null

      if (isCryptoWithdraw) {
        return (
          <CopyBlock text={(+row.original.cryptoWithdraw.amount)?.toFixed(2)}>
            {formatCurrency(
              row.original.cryptoWithdraw.amount ?? 0,
              row.original.cryptoWithdraw.currency,
            )}
          </CopyBlock>
        )
      }

      return (
        <CopyBlock text={row.original.amount?.toFixed(2)}>
          {formatCurrency(row.original.amount ?? 0, Currencies.USDT)}
        </CopyBlock>
      )
    },
  },
  {
    accessorKey: 'comment',
    header: 'Комментарий',
    cell: ({ row }) => {
      const isCryptoWithdraw = row.original.cryptoWithdraw !== null

      if (isCryptoWithdraw) {
        const transactionHash = row.original.cryptoWithdraw.transactionHash
        return (
          transactionHash && (
            <a
              href={`https://tronscan.org/#/transaction/${transactionHash}`}
              className="flex items-center gap-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="font-medium text-primary dark:text-primary-dark">
                {truncateHash(transactionHash)}
              </span>
              <TransactionIcon />
            </a>
          )
        )
      }

      return <CommentCell comment={row.original.comment} />
    },
  },
]
